import Phaser from "phaser";
import i18next from "i18next";
import { ItemSlot } from "../../../ui/item-slot";
import { SpriteLoader } from "../../../sprite-loader";
import { WindowManager } from "../../../ui/window-manager";
import { COLORS } from "../../../ui/colors";
import { BaseWindow } from "../../../ui/windows/base-window";
import { playSound } from "../../../utils/sound-utils";
import { ModernButton } from "../../../ui/modern-button";
import { trackEvent } from "../../../utils/ga-events";
import { Item, ItemDefinition, Recipe, TILE_SIZE } from "../../../models/common";
import { kebabToHumanReadable } from "../../../utils/strings";
import { getSafeAreaInsetLeft } from "../../../utils/mobile-utils";

export class CraftingWindow extends BaseWindow {
  private recipeInfoContainer: Phaser.GameObjects.Container | null = null;
  private slots: ItemSlot[] = [];
  private buttons: Phaser.GameObjects.Container[] = [];
  private recipes: Recipe[] = [];
  private currentStationName: string | null = null;
  private layoutMode: "compact" | "fullscreen" = "compact";

  constructor(scene: Phaser.Scene) {
    super(scene);
  }

  public setLayoutMode(mode: "compact" | "fullscreen"): void {
    this.layoutMode = mode;
  }

  protected getLayoutOverrides(): Partial<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> {
    if (this.layoutMode === "fullscreen") {
      // Full-screen mode: fill whole screen with margins
      const gameWidth = this.scene.scale.width;
      const gameHeight = this.scene.scale.height;
      const marginRight = 32;
      const marginLeft = 120 + getSafeAreaInsetLeft();
      const marginY = 32;
      const navPadding = this.scene.scene.key === "BaseScene" ? 88 : 0;

      return {
        x: marginLeft,
        y: 40,
        width: gameWidth - marginLeft - marginRight,
        height: gameHeight - 40 - marginY - navPadding,
      };
    } else {
      // Compact mode: fixed small size
      return {};
    }
  }

  getCurrentStationName(): string | null {
    return this.currentStationName;
  }

  setRecipes(recipes: Recipe[]) {
    this.recipes = recipes;
  }

  show(
    items: ItemDefinition[] = [],
    recipes: Recipe[] = [],
    stationName?: string,
    stationLevel?: number,
    upgradeMaterials: { itemId: number; quantity: number }[] = [],
    canUpgrade: boolean = false,
  ) {
    this.hide();
    this.recipes = recipes;
    this.currentStationName = stationName ?? null;

    const baseName = stationName ? kebabToHumanReadable(stationName) : i18next.t("ui:crafting");
    const titleWithLevel = baseName;

    // Create base window elements (background, title, close button)
    const baseElements = this.createBaseWindow({
      title: titleWithLevel,
      side: "left",
    });

    // Get window bounds for positioning child elements
    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    // (LVL X) label in yellow next to the title
    const titleWidth = this.titleText ? this.titleText.width : 0;
    const lvlLabel = this.scene.add.text(
      x + this.CONTENT_PADDING_X + titleWidth + 8,
      y + 25,
      stationLevel !== undefined ? `(LVL ${stationLevel})` : "",
      {
        fontSize: "16px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        fontStyle: "bold",
      },
    );
    lvlLabel.setOrigin(0, 0.5);
    lvlLabel.setScrollFactor(0);

    // Recipe info container will be populated when item is selected

    // Default instruction text (white)
    const infoWidth = width - this.CONTENT_PADDING_X * 2;
    let defaultInstructionText = i18next.t("ui:instructions.selectRecipe");
    if (recipes.length === 0) {
      defaultInstructionText = i18next.t("ui:instructions.noRecipes");
    }
    const instructionText = this.scene.add.text(
      x + width / 2,
      y + height - 100,
      defaultInstructionText,
      {
        fontSize: "14px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
        wordWrap: { width: infoWidth },
      },
    );
    instructionText.setOrigin(0.5, 0);
    instructionText.setScrollFactor(0);

    // Recipe name label (yellow) shown next to instructionText when recipe selected
    const recipeNameLabel = this.scene.add.text(
      x + width / 2,
      y + height - 100,
      "",
      {
        fontSize: "14px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        fontStyle: "bold",
      },
    );
    recipeNameLabel.setOrigin(0, 0);
    recipeNameLabel.setScrollFactor(0);
    recipeNameLabel.setVisible(false);

    // Grid setup
    const contentWidth = width - this.CONTENT_PADDING_X * 2;
    // Use ItemSlot's standard size
    const tempSlot = new ItemSlot(0, 0);
    const slotSize = tempSlot.size;
    const gridCols = Math.min(
      6,
      Math.max(1, Math.floor(contentWidth / (slotSize + 16))),
    );
    // Count total slots (recipes + upgrade slot if available)
    const totalSlots = recipes.length + (canUpgrade ? 1 : 0);
    const gridRows = Math.max(1, Math.ceil(totalSlots / gridCols));

    // Fixed slot spacing
    const slotSpacingX = 8; // px, adjust as needed
    const slotSpacingY = 8;
    // Left-align the grid
    const gridStartX = x + this.CONTENT_PADDING_X;
    const gridStartY = y + this.CONTENT_PADDING_Y + 20;
    let selectedItemIdx: number | null = null;
    let craftButton: Phaser.GameObjects.Container | null = null;
    let upgradeButton: Phaser.GameObjects.Container | null = null;
    let closeButton: Phaser.GameObjects.Container | null = null;
    const bottomButtonY = y + height - 10;
    const actionButtonGap = 32;

    const updateBottomButtonsLayout = () => {
      if (!closeButton) return;
      const actionButton = craftButton || upgradeButton;
      const centerX = x + width / 2;
      if (!actionButton) {
        closeButton.setPosition(centerX, bottomButtonY);
        return;
      }

      const closeBounds = closeButton.getBounds();
      const actionBounds = actionButton.getBounds();
      const closeWidth = closeBounds.width || 120;
      const actionWidth = actionBounds.width || 120;
      const centerDistance = (closeWidth + actionWidth) / 2 + actionButtonGap;

      closeButton.setPosition(centerX - centerDistance / 2, bottomButtonY);
      actionButton.setPosition(centerX + centerDistance / 2, bottomButtonY);
    };

    // Create craftable items from recipes
    const craftableItems: ItemDefinition[] = [];
    const itemSprites: Phaser.GameObjects.GameObject[] = [];

    for (let recipeIdx = 0; recipeIdx < recipes.length; recipeIdx++) {
      const recipe = recipes[recipeIdx];
      const resultItem = items.find((item) => item.id === recipe.itemId);
      if (resultItem) {
        craftableItems.push(resultItem);
      }
    }

    // Add upgrade slot after all recipe slots (only if a higher level exists)
    let upgradeSlot: ItemSlot | null = null;
    let redrawUpgradeSlot: ((selected: boolean) => void) | null = null;

    if (canUpgrade) {
      const upgradeSlotIdx = craftableItems.length;
      const upgradeRow = Math.floor(upgradeSlotIdx / gridCols);
      const upgradeCol = upgradeSlotIdx % gridCols;
      const upgradeSlotX = gridStartX + upgradeCol * (slotSize + slotSpacingX);
      const upgradeSlotY = gridStartY + upgradeRow * (slotSize + slotSpacingY);
      // Center coordinates for both slot and arrow
      const centerX = upgradeSlotX + slotSize / 2;
      const centerY = upgradeSlotY + slotSize / 2;

      // Place the upgrade arrow sprite centered in the upgrade slot
      const upgradeArrow = SpriteLoader.createSprite(
        this.scene,
        "items",
        0,
        2,
        centerX,
        centerY,
        true,
      );
      upgradeArrow.displayWidth = slotSize * 0.75;
      upgradeArrow.displayHeight = slotSize * 0.75;
      upgradeArrow.setScrollFactor(0);
      itemSprites.push(upgradeArrow);

      // Create the slot so that its rectangle is centered at the same point as the arrow
      const slot = new ItemSlot(centerX - slotSize / 2, centerY - slotSize / 2, true);
      upgradeSlot = slot;
      slot.createRectangle(this.scene);
      // Tint the slot green to distinguish it
      if (slot.rect && slot.rect instanceof Phaser.GameObjects.Graphics) {
        slot.rect.clear();
        slot.rect.fillStyle(0x1a6b2a, 0.9);
        slot.rect.fillRoundedRect(0, 0, slotSize, slotSize, 6);
        slot.rect.lineStyle(2, 0x2ecc71, 1);
        slot.rect.strokeRoundedRect(0, 0, slotSize, slotSize, 6);
        // Fix hit area to slot bounds only
        slot.rect.setInteractive(
          new Phaser.Geom.Rectangle(0, 0, slotSize, slotSize),
          Phaser.Geom.Rectangle.Contains,
        );
      }
      // Do NOT push slot into this.slots — it must not participate in recipe selection

      redrawUpgradeSlot = (selected: boolean) => {
        if (slot.rect && slot.rect instanceof Phaser.GameObjects.Graphics) {
          slot.rect.clear();
          slot.rect.fillStyle(0x1a6b2a, 0.9);
          slot.rect.fillRoundedRect(0, 0, slotSize, slotSize, 6);
          slot.rect.lineStyle(selected ? 3 : 2, selected ? 0xffffff : 0x2ecc71, 1);
          slot.rect.strokeRoundedRect(0, 0, slotSize, slotSize, 6);
        }
      };

      // ...existing code...

      if (slot.rect) {
        slot.rect.on("pointerdown", () => {
          playSound(this.scene, "menu-click-sound", null, 0.5);

          // Deselect recipe slots and restore upgrade slot to green
          this.slots.forEach((s) => { s.setSelected(false); s.applyBorderStyle(); });
          redrawUpgradeSlot!(true);
          slot.rect?.setInteractive(
            new Phaser.Geom.Rectangle(0, 0, slotSize, slotSize),
            Phaser.Geom.Rectangle.Contains,
          );

          // Clear previous recipe info
          if (this.recipeInfoContainer) {
            this.recipeInfoContainer.destroy();
            this.recipeInfoContainer = null;
          }

          if (upgradeMaterials.length === 0) {
            recipeNameLabel.setVisible(false);
            instructionText.setText(i18next.t("ui:instructions.stationMaxLevel"));
            instructionText.setPosition(x + width / 2, y + height - 120);
            instructionText.setOrigin(0.5, 0);
            return;
          }

          // Show upgrade materials label
          const labelY = y + height - 120;
          recipeNameLabel.setText(i18next.t("ui:instructions.upgradeStation"));
          recipeNameLabel.setVisible(true);
          const suffix = i18next.t("ui:instructions.requiredMaterials");
          instructionText.setText(suffix);
          const totalW = recipeNameLabel.width + instructionText.width;
          const centerX = x + width / 2;
          recipeNameLabel.setPosition(centerX - totalW / 2, labelY);
          recipeNameLabel.setOrigin(0, 0);
          instructionText.setPosition(centerX - totalW / 2 + recipeNameLabel.width, labelY);
          instructionText.setOrigin(0, 0);

          // Show upgrade material slots
          const recipeElements: Phaser.GameObjects.GameObject[] = [];
          const materialStartY = y + height - 70;
          const materialSpacing = 32;
          const materialsPerRow = Math.min(upgradeMaterials.length, 4);
          const slotSpacingUpg = 40 + materialSpacing;
          const contentCenterX = x + this.CONTENT_PADDING_X + (width - this.CONTENT_PADDING_X * 2) / 2;
          const materialStartX = contentCenterX - ((materialsPerRow - 1) * slotSpacingUpg) / 2;

          upgradeMaterials.forEach((material, idx) => {
            const matItem = items.find((i) => i.id === material.itemId);
            if (!matItem) return;
            const matRow = Math.floor(idx / materialsPerRow);
            const matCol = idx % materialsPerRow;
            const matX = materialStartX + matCol * (40 + materialSpacing);
            const matY = materialStartY + matRow * 60;
            const playerHas = this.countPlayerMaterial(material.itemId);
            const hasEnough = playerHas >= material.quantity;

            const matBg = this.scene.add.rectangle(matX, matY, 40, 40, hasEnough ? 0x2a2a2a : 0x3a1a1a, 1);
            matBg.setOrigin(0.5);
            matBg.setStrokeStyle(2, 0x666666, 1);
            matBg.setScrollFactor(0);
            recipeElements.push(matBg);

            if (matItem.spriteSheet && typeof matItem.spriteX === "number" && typeof matItem.spriteY === "number") {
              const matSprite = SpriteLoader.createSprite(this.scene, matItem.spriteSheet, matItem.spriteX, matItem.spriteY, matX, matY, true);
              matSprite.displayWidth = TILE_SIZE;
              matSprite.displayHeight = TILE_SIZE;
              matSprite.setScrollFactor(0);
              if (!hasEnough) matSprite.setTint(0xff6666);
              recipeElements.push(matSprite);
            }

            const qtyText = this.scene.add.text(matX + 18, matY - 18, `x${material.quantity}`, {
              fontSize: "14px", color: hasEnough ? "#fff" : COLORS.ORANGE, fontFamily: "Anton", fontStyle: "bold",
            });
            qtyText.setOrigin(0.5);
            qtyText.setScrollFactor(0);
            recipeElements.push(qtyText);

            const countText = this.scene.add.text(matX + 18, matY + 18, `(${playerHas})`, {
              fontSize: "14px", color: hasEnough ? COLORS.GREEN : COLORS.ORANGE, fontFamily: "Anton",
            });
            countText.setOrigin(0.5);
            countText.setScrollFactor(0);
            recipeElements.push(countText);
          });

          this.recipeInfoContainer = this.scene.add.container(0, 0, recipeElements);
          this.recipeInfoContainer.setScrollFactor(0);
          this.recipeInfoContainer.setDepth(201);
          if (this.container) this.container.add(this.recipeInfoContainer);

          // Show UPGRADE button if player has all materials
          if (upgradeButton) {
            if (this.container) this.container.remove(upgradeButton);
            const upgradeButtonIndex = this.buttons.indexOf(upgradeButton);
            if (upgradeButtonIndex > -1) this.buttons.splice(upgradeButtonIndex, 1);
            upgradeButton.destroy();
            upgradeButton = null;
            updateBottomButtonsLayout();
          }

          if (craftButton) {
            if (this.container) this.container.remove(craftButton);
            const craftButtonIndex = this.buttons.indexOf(craftButton);
            if (craftButtonIndex > -1) this.buttons.splice(craftButtonIndex, 1);
            craftButton.destroy();
            craftButton = null;
            updateBottomButtonsLayout();
          }

          const canUpgradeNow = upgradeMaterials.every(
            (m) => this.countPlayerMaterial(m.itemId) >= m.quantity,
          );
          if (canUpgradeNow) {
            upgradeButton = new ModernButton({
              scene: this.scene,
              x: x + width / 2,
              y: bottomButtonY,
              text: i18next.t("ui:buttons.upgrade"),
              style: "success",
              onClick: () => {
                if (window.socket && stationName) {
                  window.socket.emit("upgrade-station", { stationName });
                  this.hide();
                }
              },
            });
            upgradeButton.setScrollFactor(0);
            upgradeButton.setDepth(201);
            if (this.container) this.container.add(upgradeButton);
            this.buttons.push(upgradeButton);
            updateBottomButtonsLayout();
          }
        });
      }
    } // end canUpgrade

    // Create slots and display craftable items (only slots with recipes)
    for (let slotIdx = 0; slotIdx < craftableItems.length; slotIdx++) {
        const row = Math.floor(slotIdx / gridCols);
        const col = slotIdx % gridCols;
        const slotX = gridStartX + col * (slotSize + slotSpacingX);
        const slotY = gridStartY + row * (slotSize + slotSpacingY);

        const item = craftableItems[slotIdx];
        const itemSlot = new ItemSlot(slotX, slotY, true);

        // Create Phaser rectangle for the slot using ItemSlot method
        itemSlot.createRectangle(this.scene);

        this.slots.push(itemSlot);

        // Check if player can craft this item
        const recipe = recipes[slotIdx];
        const recipeEnabled = recipe.enabled !== false; // false only when station level too low
        const canCraft = recipeEnabled && this.canCraftItem(recipe);

        // Create item sprite (centered in slot)
        if (
          item.spriteSheet &&
          typeof item.spriteX === "number" &&
          typeof item.spriteY === "number"
        ) {
          const sprite = SpriteLoader.createSprite(
            this.scene,
            item.spriteSheet,
            item.spriteX,
            item.spriteY,
            slotX + slotSize / 2,
            slotY + slotSize / 2,
            true,
          );
          sprite.displayWidth = slotSize * 0.85;
          sprite.displayHeight = slotSize * 0.85;

          // Fade sprite if player can't craft or recipe is level-locked
          if (!canCraft) {
            sprite.setAlpha(0.5);
            sprite.setTint(recipeEnabled ? 0x888888 : 0x4444aa);
          }

          itemSprites.push(sprite);
        }

        // Show a small lock badge for level-locked recipes
        if (!recipeEnabled) {
          const lockBg = this.scene.add.rectangle(slotX + slotSize - 9, slotY + 9, 14, 14, 0x222266, 1);
          lockBg.setScrollFactor(0);
          const lockLabel = this.scene.add.text(slotX + slotSize - 9, slotY + 9, "🔒", { fontSize: "9px" });
          lockLabel.setOrigin(0.5).setScrollFactor(0);
          itemSprites.push(lockBg, lockLabel);
        }

        if (itemSlot.rect) {
          itemSlot.rect.on("pointerdown", () => {
            // Play menu-click sound
            playSound(this.scene, "menu-click-sound", null, 0.5);

            {
              const recipe = recipes[slotIdx];
              const recipeEnabled = recipe.enabled !== false;
              const canCraft = recipeEnabled && this.canCraftItem(recipe);

              selectedItemIdx = slotIdx;
              this.slots.forEach((s, idx) => {
                s.setSelected(idx === slotIdx);
                s.applyBorderStyle();
              });
              // Restore upgrade slot to unselected green border
              if (redrawUpgradeSlot) redrawUpgradeSlot(false);

              // Create recipe info display in the lower half
              if (this.recipeInfoContainer) {
                this.recipeInfoContainer.destroy();
                this.recipeInfoContainer = null;
              }

              // Update labels above materials section
              const labelY = y + height - 120;
              const gap = 0;
              const suffix = recipeEnabled ? i18next.t("ui:instructions.requiredMaterials") : ` - ${i18next.t("ui:instructions.unlockRecipe")}`;
              recipeNameLabel.setText(item.name);
              instructionText.setText(suffix);
              recipeNameLabel.setVisible(true);
              // After setting text, Phaser updates width — center both together
              const totalW = recipeNameLabel.width + gap + instructionText.width;
              const centerX = x + width / 2;
              recipeNameLabel.setPosition(centerX - totalW / 2, labelY);
              recipeNameLabel.setOrigin(0, 0);
              instructionText.setPosition(centerX - totalW / 2 + recipeNameLabel.width + gap, labelY);
              instructionText.setOrigin(0, 0);
              if (item && recipe) {
                const recipeElements: Phaser.GameObjects.GameObject[] = [];

                // Material items display — anchored to the bottom of the window
                const materialStartY = y + height - 70;
                const materialSpacing = 32;
                const materialsCount = recipe.materials.length;
                const materialsPerRow = Math.min(materialsCount, 4);
                const slotSpacing = 40 + materialSpacing;
                const contentCenterX =
                  x +
                  this.CONTENT_PADDING_X +
                  (width - this.CONTENT_PADDING_X * 2) / 2;
                const materialStartX =
                  contentCenterX - ((materialsPerRow - 1) * slotSpacing) / 2;

                recipe.materials.forEach((material: any, idx: number) => {
                  const matItem = items.find((i) => i.id === material.itemId);
                  if (matItem) {
                    const row = Math.floor(idx / materialsPerRow);
                    const col = idx % materialsPerRow;
                    const matX = materialStartX + col * (40 + materialSpacing);
                    const matY = materialStartY + row * 60;

                    // Check if player has enough of this material
                    const playerHas = this.countPlayerMaterial(material.itemId);
                    const hasEnough = playerHas >= material.quantity;

                    // Material background slot
                    const matBg = this.scene.add.rectangle(
                      matX,
                      matY,
                      40,
                      40,
                      hasEnough ? 0x2a2a2a : 0x3a1a1a,
                      1,
                    );
                    matBg.setOrigin(0.5);
                    matBg.setStrokeStyle(2, hasEnough ? 0x666666 : 0x666666, 1);
                    matBg.setScrollFactor(0);
                    recipeElements.push(matBg);

                    // Material sprite
                    if (
                      matItem.spriteSheet &&
                      typeof matItem.spriteX === "number" &&
                      typeof matItem.spriteY === "number"
                    ) {
                      const matSprite = SpriteLoader.createSprite(
                        this.scene,
                        matItem.spriteSheet,
                        matItem.spriteX,
                        matItem.spriteY,
                        matX,
                        matY,
                        true,
                      );
                      matSprite.displayWidth = TILE_SIZE;
                      matSprite.displayHeight = TILE_SIZE;
                      matSprite.setScrollFactor(0);

                      // Tint red if not enough materials
                      if (!hasEnough) {
                        matSprite.setTint(0xff6666);
                      }

                      recipeElements.push(matSprite);
                    }

                    // Quantity text (top right of sprite)
                    const quantityText = this.scene.add.text(
                      matX + 18,
                      matY - 18,
                      `x${material.quantity}`,
                      {
                        fontSize: "14px",
                        color: hasEnough ? "#fff" : COLORS.ORANGE,
                        fontFamily: "Anton",
                        fontStyle: "bold",
                      },
                    );
                    quantityText.setOrigin(0.5);
                    quantityText.setScrollFactor(0);
                    recipeElements.push(quantityText);

                    // Player inventory count (bottom right)
                    const playerCountText = this.scene.add.text(
                      matX + 18,
                      matY + 18,
                      `(${playerHas})`,
                      {
                        fontSize: "14px",
                        color: hasEnough ? COLORS.GREEN : COLORS.ORANGE,
                        fontFamily: "Anton",
                      },
                    );
                    playerCountText.setOrigin(0.5);
                    playerCountText.setScrollFactor(0);
                    recipeElements.push(playerCountText);
                  }
                });

                // Create container for all recipe info elements
                this.recipeInfoContainer = this.scene.add.container(
                  0,
                  0,
                  recipeElements,
                );
                this.recipeInfoContainer.setScrollFactor(0);
                this.recipeInfoContainer.setDepth(201);
                // Add recipe info container to main container for proper layering
                if (this.container) {
                  this.container.add(this.recipeInfoContainer);
                }
              }

              // Remove upgrade button when switching to recipe
              if (upgradeButton) {
                if (this.container) this.container.remove(upgradeButton);
                const upgradeIdx = this.buttons.indexOf(upgradeButton);
                if (upgradeIdx > -1) this.buttons.splice(upgradeIdx, 1);
                upgradeButton.destroy();
                upgradeButton = null;
                updateBottomButtonsLayout();
              }

              // Show CRAFT button only if player has materials
              if (craftButton) {
                // Remove from container before destroying
                if (this.container) {
                  this.container.remove(craftButton);
                }
                // Remove from buttons array
                const buttonIndex = this.buttons.indexOf(craftButton);
                if (buttonIndex > -1) {
                  this.buttons.splice(buttonIndex, 1);
                }
                craftButton.destroy();
                craftButton = null;
                updateBottomButtonsLayout();
              }

              if (canCraft) {
                craftButton = new ModernButton({
                  scene: this.scene,
                  x: x + width / 2,
                  y: bottomButtonY,
                  text: i18next.t("ui:buttons.craft"),
                  style: "success",
                  onClick: () => {
                    // Emit craft request to server
                    if (window.socket && recipe) {
                      trackEvent("item-crafted", { itemId: recipe.itemId });
                      window.socket.emit("craft-item", {
                        itemId: recipe.itemId,
                      });
                      this.hide();
                    }
                  },
                });
                craftButton.setScrollFactor(0);
                craftButton.setDepth(201);

                // Add the craft button to the main container
                if (this.container) {
                  this.container.add(craftButton);
                }
                this.buttons.push(craftButton);
                updateBottomButtonsLayout();
              } else {
                trackEvent("item-crafted-failed-missing-materials", {
                  itemId: recipe.itemId,
                });
              }
            }
          });
        }
      }

      // Create close button at bottom center
      // Move BACK button further down to avoid overlap
      closeButton = new ModernButton({
        scene: this.scene,
        x: x + width / 2,
        y: bottomButtonY,
        text: i18next.t("ui:buttons.back"),
        style: "secondary",
        onClick: () => {
          this.hide();
        },
      });
      closeButton.setScrollFactor(0);
      closeButton.setDepth(201);
      this.buttons.push(closeButton);
      updateBottomButtonsLayout();

      // Container for all elements
      // Ensure upgrade slot rect is behind the arrow sprite
      let upgradeSlotAndArrow: Phaser.GameObjects.GameObject[] = [];
      if (upgradeSlot && upgradeSlot.rect) {
        upgradeSlotAndArrow.push(upgradeSlot.rect);
        // Find the upgrade arrow in itemSprites (it is the last pushed for upgrade)
        if (itemSprites.length > 0) {
          const upgradeArrow = itemSprites[itemSprites.length - 1];
          upgradeSlotAndArrow.push(upgradeArrow);
        }
      }
      // Remove the upgrade arrow from itemSprites if present
      const filteredItemSprites = (upgradeSlotAndArrow.length > 1)
        ? itemSprites.slice(0, -1)
        : itemSprites;

      this.container = this.scene.add.container(0, 0, [
        ...baseElements,
        lvlLabel,
        recipeNameLabel,
        instructionText,
        ...this.slots.map((s) => s.rect).filter((r) => r !== null),
        ...filteredItemSprites,
        ...upgradeSlotAndArrow,
        ...this.buttons,
        // Add recipe info container if it exists (for initial render)
        ...(this.recipeInfoContainer ? [this.recipeInfoContainer] : []),
      ]);
      // Ensure upgrade slot border is above the arrow
      if (upgradeSlot && upgradeSlot.rect) {
        upgradeSlot.rect.setDepth(202);
      }
      this.container.setDepth(200);
      this.visible = true;
      this.addContainerToParent(this.container);
    }
    
  private countPlayerMaterial(materialId: number): number {
    if (typeof window === "undefined" || !(window as any).player) {
      return 0;
    }

    const player = (window as any).player;
    const storage: Item[] = player.data.storage || [];
   
    
    return (
      storage.filter((item: Item) => item?.definition?.id === materialId).length
    );
  }

  private canCraftItem(recipe: Recipe): boolean {
    if (!recipe || typeof window === "undefined" || !(window as any).player) {
      return false;
    }

    for (const material of recipe.materials) {
      if (this.countPlayerMaterial(material.itemId) < material.quantity) {
        return false;
      }
    }

    return true;
  }

  hide() {
    if (this.recipeInfoContainer) {
      this.recipeInfoContainer.destroy();
      this.recipeInfoContainer = null;
    }

    // Clean up buttons before destroying container
    this.buttons.forEach((button) => {
      if (button && !button.scene) {
        // Check if not already destroyed
        button.destroy();
      }
    });
    this.buttons = [];
    this.slots = [];
    super.hide();
  }
}
